import { useEffect, useState } from "react";
import { DatabaseSections, ModalModes } from "../../../enums/DatabaseEnums";
import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import CustomerDatabaseService from "../../../services/customerDatabase.service";
import {
  DatabaseCommonTableProps,
  AddContactsRequestType,
} from "../../../Types/DatabaseTabTypes";
import {
  addContactFields,
  addContactValidationSchema,
  addRoleValidation,
  contactsDeletedTableMap,
  contactsIdMap,
  ContactsTableConstants,
  getColumns,
} from "./constants";
import styles from "./ContactsTable.module.scss";
import TNButton from "../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../enums/ButtonAttributesEnums";
import { Input, message, Modal } from "antd";
import AddContactsForm from "./AddContactsForms";
import Form from "../../Form";
import { deleteFormSchema } from "../constants";
import DeleteConfirmation from "../DeleteConfirmation";
import QuarryDatabaseService from "../../../services/quarryDatabase.service";
import AddRoleModal from "./AddNewRoleModal";
import SearchIcon from "../../../assets/images/search-button.svg";
import ConditionalRender from "../../../reusable-components/ConditionalRender";

const ContactsTable = (props?: DatabaseCommonTableProps) => {
  const { section, sectionId, isQuarry, blockedJob } = props;
  const {
    SEARCH,
    BUTTON: { ADD_CONTACT_LABEL },
    MODAL: {
      ADD_CONTACT_MODAL_TITLE,
      SUBMIT_BUTTON_LABEL,
      WIDTH,
      EDIT_CONTACT_MODAL_TITLE,
      ZINDEX,
    },
    VALIDATION_ERRORS: {
      ATLEAST_ONE_CONTACT_ERROR,
      FAILED_TO_ADD_CONTACT,
      FAILED_TO_DELETE_CONTACT,
      FAILED_TO_UPDATE_CONTACT,
      EMAIL_ALREADY_EXISTS,
    },
    SUCCESS_MESSAGES: {
      ADD_NEW_CONTACT_MESSAGE,
      DELETE_CONTACT_MESSAGE,
      EDIT_CONTACT_MESSAGE,
    },
  } = ContactsTableConstants;
  const {
    FetchContactsTable,
    AddDatabaseTableEntries,
    DeleteDatabaseTableEntries,
    EditDatabaseTableEntries,
  } = CustomerDatabaseService();
  const { SetPrimaryContactStatus } = QuarryDatabaseService();
  const { mutateAsync: addContacts, isPending } = AddDatabaseTableEntries();
  const { mutateAsync: deleteContact, isPending: isDeletePending } =
    DeleteDatabaseTableEntries();
  const { mutateAsync: editContact, isPending: isEditPending } =
    EditDatabaseTableEntries();
  const { mutateAsync: setPrimaryStatus, isPending: setPrimaryPending } =
    SetPrimaryContactStatus();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [addContactDefaultValue, setAddContactDefaultValue] =
    useState<AddContactsRequestType>({});
  const [contactRole, setContactRole] = useState<string>(undefined);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [entityId, setEntityId] = useState<string>("");
  const [modalMode, setModalMode] = useState<ModalModes>(ModalModes.CREATE);

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortValue, setSortValue] = useState({});

  const handleContactModalOpen = () => setIsContactModalOpen(true);
  const handleContactModalClose = () => setIsContactModalOpen(false);
  const handleDeleteModalOpen = (id?: string) => {
    setEntityId(id);
    setDeleteModalOpen(true);
  };
  const [isAddRoleOpen, setIsAddRoleOpen] = useState<boolean>();
  const handleRoleModalOpen = () => setIsAddRoleOpen(true);
  const handleRoleModalClose = () => setIsAddRoleOpen(false);
  const handleDeleteModalClose = () => setDeleteModalOpen(false);

  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };
  const handlePagination = (page: number) => handleCurrentPage(page);
  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchInput = (value: string) => {
    setSearchInput(value);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const handleSorterAction = (sorter: {
    order?: string;
    columnKey?: string;
  }) => {
    if (!sorter?.order) {
      setSortValue({});
      return;
    }
    setSortValue({
      sortBy: sorter?.columnKey,
      sortOrder: sorter?.order,
    });
  };

  const handleDelete = async (formVal?: object) => {
    await deleteContact(
      {
        id: entityId,
        deletedTable: contactsDeletedTableMap[section],
        notes: formVal?.archiveReason,
        ...(contactsIdMap[section]
          ? { [contactsIdMap[section]]: sectionId }
          : {}),
      },
      {
        onSuccess: () => {
          message.success(DELETE_CONTACT_MESSAGE);
          handleDeleteModalClose();
          refetch();
        },
        onError: () => {
          message.error(FAILED_TO_DELETE_CONTACT);
        },
      },
    );
  };

  const handleEdit = (id?: string) => {
    const contactData = data?.data?.find((val) => val?.id === id);
    if (contactData) {
      const [firstName, lastName] = contactData?.contactName?.split(" ");
      setModalMode(ModalModes.EDIT);
      setEntityId(id);
      setAddContactDefaultValue({
        firstName: firstName,
        lastName: lastName,
        email: contactData?.email,
        phone: contactData?.phone,
        roleId: contactData?.roleId,
        notes: contactData?.notes,
      });
      handleContactModalOpen();
    } else {
      message.error(FAILED_TO_UPDATE_CONTACT);
    }
  };

  const handleSetPrimaryStatus = async (
    record: any,
    primaryStatus: boolean,
  ) => {
    const [firstName, lastName] = record?.contactName?.split(" ");
    const { isPrimary, contactName, ...rest } = record;
    const payload = {
      ...rest,
      firstName: firstName,
      lastName: lastName,
      isPrimaryContact: primaryStatus,
    };
    delete payload?.id;
    await setPrimaryStatus(
      {
        payloadData: payload,
        sectionId: sectionId,
        entityId: record?.id,
      },
      {
        onSuccess: () => {
          refetch();
        },
      },
    );
  };

  const { data, refetch, isFetching } = FetchContactsTable({
    section: section,
    section_id: sectionId,
    page: currentPage,
    perPage: pageSize,
    search: searchTerm,
    ...sortValue,
  });
  const columns = getColumns({
    handleDeleteModalOpen: handleDeleteModalOpen,
    handleEditNavigate: handleEdit,
    handleSetPrimary: handleSetPrimaryStatus,
    isQuarry: isQuarry,
    blockedJob: blockedJob,
    section: section,
    ...sortValue,
  });
  useEffect(() => {
    refetch();
  }, [pageSize, currentPage, searchTerm, sortValue]);

  return (
    <>
      <Form validationSchema={deleteFormSchema}>
        {({ control: { handleSubmit } }) => (
          <DeleteConfirmation
            tabType={DatabaseSections.CONTACTS}
            deletePending={isDeletePending}
            isOpen={isDeleteModalOpen}
            handleClose={handleDeleteModalClose}
            submitActionCall={handleSubmit(handleDelete)}
          />
        )}
      </Form>
      <Form
        validationSchema={addContactValidationSchema}
        values={addContactDefaultValue}
        key={JSON.stringify(addContactDefaultValue)}
      >
        {({
          getValues,
          clearErrors,
          setError,
          control: { handleSubmit, _reset, _formValues },
        }) => {
          const handleModalCancel = async () => {
            _reset({
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              roleId: [],
              notes: "",
            });
            clearErrors();
            handleContactModalClose();
            setAddContactDefaultValue({});
            setEntityId("");
            setModalMode(ModalModes.CREATE);
          };
          const handleModalSubmit = async () => {
            const email = getValues(addContactFields.email);
            const phone = getValues(addContactFields.phone);

            if (!email && !phone) {
              message.error(ATLEAST_ONE_CONTACT_ERROR);
            } else {
              if (modalMode === ModalModes.CREATE) {
                await addContacts(
                  {
                    ..._formValues,
                    contactRole,
                    id: DatabaseSections.CONTACTS,
                    section: section,
                    section_id: sectionId,
                  },
                  {
                    onSuccess: () => {
                      message.success(ADD_NEW_CONTACT_MESSAGE);
                      handleContactModalClose();
                      _reset({
                        firstName: "",
                        lastName: "",
                        email: "",
                        phone: "",
                        roleId: [],
                        notes: "",
                      });
                      setContactRole(undefined);
                      refetch();
                      setAddContactDefaultValue({});
                      handleContactModalClose();
                    },
                    onError: (error) => {
                      message.error(
                        error?.response?.data?.message ?? FAILED_TO_ADD_CONTACT,
                      );
                    },
                  },
                );
              }
              if (modalMode === ModalModes.EDIT) {
                await editContact(
                  {
                    ..._formValues,
                    contactRole,
                    id: DatabaseSections.CONTACTS,
                    section: section,
                    section_id: sectionId,
                    entityId: entityId,
                  },
                  {
                    onSuccess: () => {
                      message.success(EDIT_CONTACT_MESSAGE);
                      handleContactModalClose();
                      _reset({
                        firstName: "",
                        lastName: "",
                        email: "",
                        phone: "",
                        roleId: [],
                        notes: "",
                      });
                      setContactRole(undefined);
                      refetch();
                      setAddContactDefaultValue({});
                      handleContactModalClose();
                      setEntityId("");
                      setModalMode(ModalModes.CREATE);
                    },
                    onError: (error) => {
                      message.error(
                        error?.response?.data?.message ?? FAILED_TO_ADD_CONTACT,
                      );
                    },
                  },
                );
              }
            }
          };
          return (
            <Modal
              okText={SUBMIT_BUTTON_LABEL}
              onOk={handleSubmit(handleModalSubmit)}
              okButtonProps={{
                loading:
                  modalMode === ModalModes.CREATE ? isPending : isEditPending,
              }}
              visible={isContactModalOpen}
              onCancel={handleModalCancel}
              title={
                modalMode === ModalModes.CREATE
                  ? ADD_CONTACT_MODAL_TITLE
                  : EDIT_CONTACT_MODAL_TITLE
              }
              width={WIDTH}
              maskClosable={false}
              destroyOnClose={true}
              zIndex={ZINDEX}
            >
              <AddContactsForm
                setContactRole={setContactRole}
                handleOpenAddRole={handleRoleModalOpen}
              />
            </Modal>
          );
        }}
      </Form>
      <Form validationSchema={addRoleValidation}>
        {() => (
          <AddRoleModal
            isOpen={isAddRoleOpen}
            handleClose={handleRoleModalClose}
          />
        )}
      </Form>
      <ConditionalRender visible={section === DatabaseSections.CUSTOMER}>
        <Input
          placeholder={SEARCH}
          className={styles.searchField}
          onChange={(e) => handleSearchInput(e.target.value)}
          addonAfter={
            <TNButton
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.SMALL}
              block
              text={<img className={styles.searchFieldIcon} src={SearchIcon} />}
              onClick={handleSearch}
            />
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch();
            }
          }}
        />
      </ConditionalRender>
      <CustomTableWithPagination
        columns={columns}
        data={data?.data}
        pageSize={pageSize}
        currentPage={currentPage}
        rowKey={(record) => record?.id}
        totalData={data?.meta?.totalRecords}
        handleSizeChangerChange={handlePaginationPageSize}
        handleChange={handlePagination}
        loading={isFetching}
        className={styles.Table}
        isSortable={section === DatabaseSections.CUSTOMER}
        handleSorter={handleSorterAction}
      />
      <TNButton
        text={ADD_CONTACT_LABEL}
        type={ButtonType.PRIMARY}
        size={ButtonSize.LARGE}
        shape={ButtonShape.DEFAULT}
        className={styles.addContactButton}
        onClick={handleContactModalOpen}
        disabled={!!blockedJob}
      />
    </>
  );
};

export default ContactsTable;
